import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { STEP_STATE } from '../lib/constants';

import {
  findStepById,
  getStepId,
  handleFirstIncompleteStep,
  handleInvalidStepId,
  handleMissingStepId,
  handleStepWithSubsteps,
  handleStepWithoutSubsteps,
  hasAnyCompletedStep,
  isIntroStep,
  isReviewStep,
  isSpecialStep,
  parseUrlStepId,
  syncUrlFromState
} from './stepperUtils';

/**
 * Manages wizard steps state and navigation.
 *
 * @param {Array} initialSteps - Steps from server/props.
 * @param {Object} options - Configuration options
 * @param {boolean} options.syncWithUrl - Whether to sync navigation with URL
 * @param {string} options.baseUrl - Base URL for navigation (e.g., '/admin/practices/123/onboarding')
 * @returns {{
 *   stepsState: Array,
 *   setStepsState: Function,
 *   activeStep: Object|undefined,
 *   activeSubstep: Object|undefined,
 *   flattenedSubsteps: Array,
 *   replaceWithInitial: Function,
 *   expandStep: Function,
 *   activateStep: Function,
 *   navigateToSubstep: Function,
 *   navigateTo: Function,
 *   goNext: Function,
 *   goPrev: Function
 * }}
 */
export default function useStepper(initialSteps = [], options = {}) {
  const { syncWithUrl = false, baseUrl = '' } = options;
  const [stepsState, setStepsState] = useState([]);
  const params = useParams();
  const navigate = useNavigate();
  const [isInitialized, setIsInitialized] = useState(false);
  const isNavigatingFromUrl = useRef(false);
  const isNavigatingProgrammatically = useRef(false);
  const justSyncedUrlFromState = useRef(false);

  const replaceWithInitial = useCallback((incoming) => {
    if (!Array.isArray(incoming) || incoming.length === 0) {
      setStepsState([]);
      return;
    }
    setStepsState((prev) =>
      incoming.map((step, sIndex) => {
        const prevStep = prev?.[sIndex];

        const mappedSubsteps =
          step.substeps?.map((sub, subIndex) => {
            const prevSub = prevStep?.substeps?.[subIndex];
            return {
              ...sub,
              active: prevSub?.active ?? (sIndex === 0 && subIndex === 0)
            };
          }) ?? [];

        return {
          ...step,
          active: prevStep?.active ?? sIndex === 0,
          state_status:
            prevStep?.state_status ?? (sIndex === 0 ? STEP_STATE.EXPANDED : STEP_STATE.COLLAPSED),
          substeps: mappedSubsteps
        };
      })
    );
  }, []);

  useEffect(() => {
    replaceWithInitial(initialSteps);
    if (initialSteps && initialSteps.length > 0) {
      setIsInitialized(true);
    }
  }, [initialSteps, replaceWithInitial]);

  const flattenedSubsteps = useMemo(
    () =>
      stepsState.flatMap((step) =>
        (step.substeps || []).map((sub) => ({
          ...sub,
          stepCode: step.code
        }))
      ),
    [stepsState]
  );

  const activeSubstep = useMemo(() => flattenedSubsteps.find((s) => s.active), [flattenedSubsteps]);

  const activeStep = useMemo(
    () =>
      stepsState.find((s) => s.active) ||
      stepsState.find((s) => s.substeps?.some((sub) => sub.active)),
    [stepsState]
  );

  const expandStep = useCallback((stepCode) => {
    setStepsState((prev) =>
      prev.map((step) => {
        if (step.code !== stepCode) return step;

        const isExpanded = step.state_status === STEP_STATE.EXPANDED;

        return {
          ...step,
          state_status: isExpanded ? STEP_STATE.COLLAPSED : STEP_STATE.EXPANDED
        };
      })
    );
  }, []);

  const collapseStep = useCallback((stepCode) => {
    setStepsState((prev) =>
      prev.map((step) =>
        step.code === stepCode ? { ...step, state_status: STEP_STATE.COLLAPSED } : step
      )
    );
  }, []);

  const activateStep = useCallback((stepCode, { fromUrl = false } = {}) => {
    if (!fromUrl) {
      isNavigatingProgrammatically.current = true;
    }
    setStepsState((prev) => {
      const currentExpanded = prev.find((s) => s.state_status === STEP_STATE.EXPANDED)?.code;

      if (currentExpanded === stepCode) {
        return prev;
      }

      return prev.map((step) => {
        const isTarget = step.code === stepCode;
        const nextState = isTarget ? STEP_STATE.EXPANDED : STEP_STATE.COLLAPSED;

        const substeps = Array.isArray(step.substeps) ? step.substeps : [];
        const updatedSubsteps = substeps.map((sub, idx) => ({
          ...sub,
          active: isTarget ? idx === 0 : false
        }));

        return {
          ...step,
          active: isTarget,
          state_status: nextState,
          ...(Array.isArray(step.substeps) ? { substeps: updatedSubsteps } : {})
        };
      });
    });
  }, []);

  const navigateToSubstep = useCallback((substepCode, { fromUrl = false } = {}) => {
    if (!fromUrl) {
      isNavigatingProgrammatically.current = true;
    }
    setStepsState((prev) =>
      prev.map((step) => {
        const contains = step.substeps?.some((s) => s.code === substepCode);
        const nextStatus = contains
          ? STEP_STATE.EXPANDED
          : step.state_status === STEP_STATE.EXPANDED
            ? STEP_STATE.COLLAPSED
            : step.state_status;

        return {
          ...step,
          active: contains,
          state_status: nextStatus,
          substeps: step.substeps?.map((s) => ({ ...s, active: s.code === substepCode })) ?? []
        };
      })
    );
  }, []);

  const navigateTo = useCallback(
    ({ stepCode, substepCode } = {}) => {
      if (substepCode) {
        // prefer navigating directly to substep when provided
        navigateToSubstep(substepCode);
        return;
      }

      if (!stepCode) return;

      setStepsState((prev) =>
        prev.map((step) => {
          const isTarget = step.code === stepCode;
          const nextState = isTarget ? STEP_STATE.EXPANDED : STEP_STATE.COLLAPSED;

          const substeps = Array.isArray(step.substeps) ? step.substeps : [];
          const updatedSubsteps = substeps.map((sub, idx) => ({
            ...sub,
            // when target step, activate its first substep, otherwise deactivate
            active: isTarget ? idx === 0 : false
          }));

          return {
            ...step,
            active: isTarget,
            state_status: nextState,
            ...(Array.isArray(step.substeps) ? { substeps: updatedSubsteps } : {})
          };
        })
      );
    },
    [navigateToSubstep]
  );

  const goNext = useCallback(() => {
    if (!flattenedSubsteps.length) return;
    const idx = flattenedSubsteps.findIndex((s) => s.active);
    if (idx > -1 && idx < flattenedSubsteps.length - 1) {
      navigateToSubstep(flattenedSubsteps[idx + 1].code);
    }
  }, [flattenedSubsteps, navigateToSubstep]);

  const goPrev = useCallback(() => {
    if (!flattenedSubsteps.length) return;
    const idx = flattenedSubsteps.findIndex((s) => s.active);
    if (idx > 0) {
      navigateToSubstep(flattenedSubsteps[idx - 1].code);
    }
  }, [flattenedSubsteps, navigateToSubstep]);

  // Sync URL params with active step/substep (URL -> State)
  useEffect(() => {
    if (!syncWithUrl || !isInitialized || stepsState.length === 0) return;

    // Skip URL-based sync when navigating programmatically (goNext/goPrev)
    if (isNavigatingProgrammatically.current) {
      return;
    }

    // Skip if we just synced URL from state (avoid re-processing our own URL change)
    if (justSyncedUrlFromState.current) {
      justSyncedUrlFromState.current = false;
      return;
    }

    const { step_id: rawStepId, substep_id } = params;

    // Parse URL step_id to handle special cases (empty → intro, 'review' → review)
    const step_id = parseUrlStepId(rawStepId);

    // Handle intro step - when URL has no step_id, activate intro step
    // If any step has already been completed, skip intro and go to first incomplete step
    if (isIntroStep(step_id)) {
      if (hasAnyCompletedStep(stepsState)) {
        handleFirstIncompleteStep({ flattenedSubsteps, stepsState, baseUrl, navigate });
        return;
      }

      const introStep = findStepById(stepsState, step_id);
      if (introStep) {
        if (!introStep.active) {
          isNavigatingFromUrl.current = true;
          activateStep(introStep.code, { fromUrl: true });
        }
        return;
      }
      // If no intro step exists, redirect to first non-intro substep
      handleMissingStepId({ flattenedSubsteps, stepsState, baseUrl, navigate });
      return;
    }

    // Handle review step - when URL is /review, activate review step
    if (isReviewStep(step_id)) {
      const reviewStep = findStepById(stepsState, step_id);
      if (reviewStep) {
        if (!reviewStep.active) {
          isNavigatingFromUrl.current = true;
          activateStep(reviewStep.code, { fromUrl: true });
        }
        return;
      }
      // If no review step exists, redirect to first substep
      handleInvalidStepId({ flattenedSubsteps, stepsState, baseUrl, navigate });
      return;
    }

    // Find step by ID from URL
    const targetStep = findStepById(stepsState, step_id);

    // Handle invalid step_id - redirect to first substep
    if (!targetStep) {
      handleInvalidStepId({ flattenedSubsteps, stepsState, baseUrl, navigate });
      return;
    }

    // Handle navigation based on whether step has substeps
    if (targetStep.substeps?.length > 0) {
      handleStepWithSubsteps({
        targetStep,
        stepId: String(step_id),
        substepId: substep_id,
        baseUrl,
        navigate,
        navigateToSubstep,
        isNavigatingFromUrl
      });
    } else {
      handleStepWithoutSubsteps({
        targetStep,
        stepId: String(step_id),
        substepId: substep_id,
        baseUrl,
        navigate,
        activateStep,
        isNavigatingFromUrl
      });
    }
  }, [
    params,
    stepsState,
    flattenedSubsteps,
    syncWithUrl,
    isInitialized,
    baseUrl,
    navigate,
    activateStep,
    navigateToSubstep
  ]);

  // Sync active step/substep state to URL (State -> URL)
  useEffect(() => {
    if (!syncWithUrl || !isInitialized || !baseUrl) return;

    // Skip if this change came from URL navigation
    if (isNavigatingFromUrl.current) {
      isNavigatingFromUrl.current = false;
      return;
    }

    const stepId = getStepId(activeStep);

    // Skip URL sync for special steps unless navigating programmatically
    if (isSpecialStep(stepId) && !isNavigatingProgrammatically.current) {
      return;
    }

    // Reset programmatic navigation flag
    if (isNavigatingProgrammatically.current) {
      isNavigatingProgrammatically.current = false;
    }

    // Mark that we're about to sync URL from state (to prevent re-processing)
    justSyncedUrlFromState.current = true;

    // Sync URL from current state
    syncUrlFromState({
      activeStep,
      activeSubstep,
      params,
      baseUrl,
      navigate
    });
  }, [activeSubstep, activeStep, syncWithUrl, isInitialized, baseUrl, navigate, params]);

  return {
    stepsState,
    setStepsState,
    activeStep,
    activeSubstep,
    flattenedSubsteps,
    replaceWithInitial,
    expandStep,
    collapseStep,
    activateStep,
    navigateTo,
    navigateToSubstep,
    goNext,
    goPrev
  };
}
